import { autobind } from "core-decorators";
import queryString from "query-string";
import { parse as parseUrl, format as formatUrl } from "url";
import $ from "zeptojs";
import store from "utils/storeFactory";
import getCanControlVolume from "selectors/device/getCanControlVolume";
import * as VerseImaEvent from "./VerseImaEvent";

function getUrl(adNetworkUrl, adNetworkOptions, playerDimensions) {
  let url = adNetworkUrl;
  const parsedUrl = parseUrl(url);
  let parsedQueryString = queryString.parse(parsedUrl.query);
  if (adNetworkOptions.generateCorrelator) {
    const correlator = `${Date.now()}`;
    parsedQueryString = {
      ...parsedQueryString,
      [adNetworkOptions.correlatorKey || "correlator"]: correlator,
    };
  }

  if (adNetworkOptions.includeUrl) {
    parsedQueryString = {
      ...parsedQueryString,
      [adNetworkOptions.urlKey || "url"]: window.location.href,
    };
  }

  if (adNetworkOptions.includeHeight) {
    parsedQueryString = {
      ...parsedQueryString,
      [adNetworkOptions.heightKey || "h"]: playerDimensions.height,
    };
  }

  if (adNetworkOptions.includeWidth) {
    parsedQueryString = {
      ...parsedQueryString,
      [adNetworkOptions.widthKey || "w"]: playerDimensions.width,
    };
  }

  parsedUrl.query = queryString.stringify(parsedQueryString);
  url = formatUrl(parsedUrl);

  return url;
}

let hasPlayedPreroll = false;

export default class VerseImaPlugin {
  constructor(player, options = {}) {
    this.player = player;
    this.options = options;
    this.initialized = false;
    this.storedMethods = new WeakMap();
    this.paused = false;
    this.unsubscribeStore = store.subscribe(this.onStoreUpdate);
  }

  /****
   *  Getters/Setters
   *****/
  get playerWidth() {
    return this.player.el().offsetWidth;
  }

  get playerHeight() {
    return this.player.el().offsetHeight;
  }

  get playerAspect() {
    return this.playerWidth / this.playerHeight;
  }

  get playerTop() {
    return this.player.el().getBoundingClientRect().top;
  }

  get shouldBeMuted() {
    return (
      store.getState().volume.muted && getCanControlVolume(store.getState())
    );
  }

  /*****
   *  Public API
   ******/
  updateOptions(newOptions) {
    this.options = {
      ...this.options,
      ...newOptions,
    };
  }

  updatePlayer(player) {
    this.player = player;
  }

  init() {
    this.adContainerDiv = document.createElement("div");
    this.adContainerDiv.className = "ima-ad-container";
    this.playerContainer = document.getElementById("ad-display-window");
    this.playerContainer.appendChild(this.adContainerDiv);
    this.mediaEl = $(this.player.el()).find(
      this.options.mediaTagName || "video",
    )[0];

    this.adDisplayContainer = new google.ima.AdDisplayContainer(
      this.adContainerDiv,
    );
    this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);
    this.adsLoader.getSettings().setAutoPlayAdBreaks(true);
    this.adsLoader
      .getSettings()
      .setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.ENABLED);
    this.adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onAdsManagerLoaded,
      false,
    );
    this.adsLoader.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError,
      false,
    );
  }

  play() {
    if (!this.options.playAd || !this.options.canPlayAds) {
      return;
    }

    if (!this.options.userInteractionRequiredBeforePlayback) {
      this._initializeAndRequestAd();
    } else if (
      this.options.userInteractionRequiredBeforePlayback &&
      this.userInteractionTriggered
    ) {
      this._initializeAndRequestAd();
    }
  }

  triggerUserInteraction() {
    if (
      this.options.userInteractionRequiredBeforePlayback &&
      !this.userInteractionTriggered
    ) {
      this.userInteractionTriggered = true;
      this.play();
    }
  }

  /*****
   *  Private API
   ******/

  _requestAds() {
    const adsRequest = new google.ima.AdsRequest();
    adsRequest.adTagUrl = getUrl(
      this.options.adNetworkUrl,
      this.options.adNetworkOptions,
      { width: this.playerWidth, height: this.playerHeight },
    );
    adsRequest.linearAdSlotWidth = 640;
    adsRequest.linearAdSlotHeight = 400;
    adsRequest.nonLinearAdSlotWidth = 640;
    adsRequest.nonLinearAdSlotHeight = 150;
    adsRequest.setAdWillAutoPlay(true);
    this.adsLoader.requestAds(adsRequest);
  }

  _initializeAdContainer() {
    if (!this.adContainerInitialized) {
      this.adDisplayContainer.initialize();
      this.adContainerInitialized = true;
    }
  }

  _initializeAndRequestAd() {
    this._initializeAdContainer();
    this._requestAds();
  }

  _start() {
    this.adsManager.init(
      this.playerWidth,
      this.playerHeight,
      google.ima.ViewMode.NORMAL,
    );
    this.adsManager.start();
    this.adsManager.setVolume(this.shouldBeMuted ? 0 : 1);
  }

  _attachEventListeners() {
    if (!this.adsManager) {
      console.error("attachEventListeners called before adsManager populated"); // eslint-disable-line no-console
      return;
    }
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.LOADED,
      this.onAdLoaded,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      this.onSkippableStateChanged,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.STARTED,
      this.onStarted,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.PAUSED,
      this.onPaused,
    );
    this.adsManager.addEventListener(
      google.ima.AdEvent.Type.CLICK,
      this.onClick,
    );

    this.player.on(VerseImaEvent.USER_REQUESTED_SKIP, this.onUserRequestedSkip);
    this.player.one("adserror", this.onAdsError);
    this.player.one("dispose", this.destroy);

    window.addEventListener("resize", this.onResize);

    this.debugImaEvents();
  }

  _detachEventListeners() {
    if (!this.adsManager) {
      console.error("detachEventListeners called without adsManager populated"); // eslint-disable-line no-console
      return;
    }
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.LOADED,
      this.onAdLoaded,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      this.onSkippableStateChanged,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.STARTED,
      this.onStarted,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.PAUSED,
      this.onPaused,
    );
    this.adsManager.removeEventListener(
      google.ima.AdEvent.Type.CLICK,
      this.onClick,
    );

    this.player.off(
      VerseImaEvent.USER_REQUESTED_SKIP,
      this.onUserRequestedSkip,
    );
    this.player.off("adserror", this.onAdsError);

    window.removeEventListener("resize", this.onResize);
  }

  debugImaEvents() {
    const DEBUG_AD_EVENTS = true;

    // https://developers.google.com/interactive-media-ads/docs/sdks/html5/v3/apis#ima.AdEvent.Type
    const imaEvents = [
      google.ima.AdEvent.Type.AD_BREAK_READY,
      google.ima.AdEvent.Type.AD_METADATA,
      google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      google.ima.AdEvent.Type.CLICK,
      google.ima.AdEvent.Type.COMPLETE,
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      google.ima.AdEvent.Type.DURATION_CHANGE,
      google.ima.AdEvent.Type.FIRST_QUARTILE,
      google.ima.AdEvent.Type.IMPRESSION,
      google.ima.AdEvent.Type.LINEAR_CHANGED,
      google.ima.AdEvent.Type.LOADED,
      google.ima.AdEvent.Type.LOG,
      google.ima.AdEvent.Type.MIDPOINT,
      google.ima.AdEvent.Type.PAUSED,
      google.ima.AdEvent.Type.RESUMED,
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      google.ima.AdEvent.Type.SKIPPED,
      google.ima.AdEvent.Type.STARTED,
      google.ima.AdEvent.Type.THIRD_QUARTILE,
      google.ima.AdEvent.Type.USER_CLOSE,
      google.ima.AdEvent.Type.VOLUME_CHANGED,
      google.ima.AdEvent.Type.VOLUME_MUTED,
    ];

    if (DEBUG_AD_EVENTS && DEVELOPMENT) {
      imaEvents.forEach((eventName) => {
        this.adsManager.addEventListener(eventName, () => {
          const remainingTime = this.adsManager.getRemainingTime();
          //eslint-disable-next-line no-console
          console.log("[IMA]", eventName, remainingTime);
        });
      }, this);
    }
  }

  /*****
   *  Event Handlers
   ******/

  @autobind
  onStoreUpdate() {
    if (this.shouldBeMuted !== this.currentMutedState) {
      this.currentMutedState = this.shouldBeMuted;
      this.onVolumeChange();
    }
  }

  @autobind
  onResize() {
    const windowSize = {
      height: window.innerHeight,
      width: window.innerWidth,
    };
    const windowAspect = windowSize.width / windowSize.height;
    let adWidth;
    let adHeight;
    let top = 0;
    let left = 0;
    const desiredAspect = 16 / 9;
    if (windowAspect < desiredAspect) {
      adWidth = windowSize.width;
      adHeight = windowSize.width / desiredAspect;
      top = (windowSize.height - adHeight) / 2;
    } else {
      adWidth = windowSize.height * desiredAspect;
      adHeight = windowSize.height;
      left = (windowSize.width - adWidth) / 2;
    }

    this.adsManager.resize(adWidth, adHeight, google.ima.ViewMode.NORMAL);
    this.playerContainer.style.top = `${top}px`;
    this.playerContainer.style.left = `${left}px`;
    this.playerContainer.style.position = "absolute";
  }

  @autobind
  onAdsManagerLoaded(adsManagerLoadedEvent) {
    if (this.options.useVerseControlledMidroll) {
      const settings = new google.ima.AdsRenderingSettings();
      let startTime = 0;
      const tempAdsManager = adsManagerLoadedEvent.getAdsManager({
        currentTime: 0,
        duration: this.player.duration(),
      });
      const cuepoints = tempAdsManager.getCuePoints();
      const hasMidrollAds = cuepoints.length > 0 && cuepoints[1] > 0;

      if (hasMidrollAds && hasPlayedPreroll) {
        settings.playAdsAfterTime = cuepoints[1] - 1;
        startTime = cuepoints[1];
      }

      // give a fake start time so it sticks and doesn't play other ads
      this.adsManager = adsManagerLoadedEvent.getAdsManager(
        {
          currentTime: startTime,
          duration: this.player.duration(),
        },
        settings,
      );

      hasPlayedPreroll = true;
    } else {
      this.adsManager = adsManagerLoadedEvent.getAdsManager(this.mediaEl);
    }

    this._attachEventListeners();
    this._start();
  }

  @autobind
  onVolumeChange() {
    if (this.adsManager) {
      this.adsManager.setVolume(this.shouldBeMuted ? 0 : 1);
    }
  }

  @autobind
  onClick() {
    this.player.pause();
  }

  @autobind
  onAdError() {
    //console.log("[IMA] ERROR", adErrorEvent.getError());
    this.player.trigger(VerseImaEvent.AD_NOT_PLAYING);
    this.player.trigger(VerseImaEvent.AD_COMPLETE);
    if (this.adsManager) {
      this.adsManager.destroy();
    }
  }

  @autobind
  onAdLoaded() {
    this.onResize();
    this.adsManager.setVolume(this.shouldBeMuted ? 0 : 1);
    const remainingTime = this.adsManager.getRemainingTime();
    this.adDuration = remainingTime;
    this.player.trigger(VerseImaEvent.AD_LOADED);
    this.player.trigger(
      new CustomEvent(VerseImaEvent.AD_IS_SKIPPABLE_CHANGE, {
        detail: {
          value: this.adsManager.getCurrentAd().isSkippable(),
        },
      }),
    );

    this.player.trigger(
      new CustomEvent(VerseImaEvent.AD_DURATION_CHANGE, {
        detail: {
          duration: this.adDuration,
        },
      }),
    );
  }

  @autobind
  onSkippableStateChanged() {
    const isAdSkipAllowedNow = this.adsManager.getAdSkippableState();

    this.player.trigger(
      new CustomEvent(VerseImaEvent.AD_IS_SKIP_ALLOWED_NOW_CHANGE, {
        detail: {
          value: isAdSkipAllowedNow,
        },
      }),
    );
  }

  @autobind
  onStarted() {
    this.player.trigger(VerseImaEvent.AD_PLAYING);
  }

  @autobind
  onContentResumeRequested() {
    if (this.contentAlreadyResumed) {
      return;
    }
    if (this.options.useVerseControlledMidroll) {
      this.adsManager.discardAdBreak();
      this.adsManager.destroy();
      this.adContainerInitialized = false;
    }
    this.contentAlreadyResumed = true;
    if (this.adContainerDiv) {
      this.adContainerDiv.style.display = "none";
    }
    this.restoreStoredMethods();

    this.player.trigger(VerseImaEvent.AD_NOT_PLAYING);
    this.player.trigger(VerseImaEvent.AD_COMPLETE);

    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
    }
  }

  @autobind
  onContentPauseRequested() {
    this.contentAlreadyResumed = false;
    this.player.pause();
    if (this.adContainerDiv) {
      this.adContainerDiv.style.display = "block";
    }
    this.hijackPlayerMethods();
  }

  @autobind
  onPaused() {
    this.player.trigger(VerseImaEvent.AD_PAUSED);
  }

  @autobind
  onResumed() {
    this.player.trigger(VerseImaEvent.AD_RESUMED);
  }

  @autobind
  onAdsError() {
    this.onContentResumeRequested();
  }

  @autobind
  onUserRequestedSkip() {
    this.adsManager.skip();
  }

  /******
   *  Util methods
   *******/
  @autobind
  tick() {
    if (!this.paused && !this.unloading) {
      const remainingTime = this.adsManager.getRemainingTime();
      this.dispatchTimeUpdate(this.adDuration - remainingTime);
    }

    if (!this.unloading) {
      this.animationFrame = window.requestAnimationFrame(this.tick);
    }
  }

  hijackPlayerMethods() {
    this.storedMethods.set(this.player, {
      pause: this.player.pause,
      paused: this.player.paused,
      play: this.player.play,
    });

    this.player.pause = () => {
      this.paused = true;
      this.adsManager.pause();
    };

    this.player.play = () => {
      this.paused = false;
      this.adsManager.resume();
    };

    this.player.paused = () => this.paused;
    this.tick();
  }

  restoreStoredMethods() {
    const localStoredMethods = this.storedMethods.get(this.player);

    if (localStoredMethods) {
      Object.keys(localStoredMethods).forEach((key) => {
        this.player[key] = localStoredMethods[key];
      });

      // Remove `player` from `storedMethods` because we don't need it anymore.
      this.storedMethods.delete(this.player);
    }
  }

  dispatchTimeUpdate(time) {
    this.player.trigger(
      new CustomEvent(VerseImaEvent.AD_TIME_UPDATE, {
        detail: {
          time,
        },
      }),
    );
  }

  @autobind
  destroy() {
    this.unloading = true;
    this.playerContainer.removeChild(this.adContainerDiv);
    this._detachEventListeners();
    this.unsubscribeStore();
  }
}
